import { Context, createContext, ReactNode, useState } from "react";

interface TabNavigationContextType {
  sectionRefs: Record<string, { title: string; ref: HTMLElement | null }>;
  addSectionRef: (id: string, title: string, ref: HTMLElement | null) => void;
  removeSectionRef: (id: string) => void;
  sectionErrors: Record<string, string[]>;
  setSectionErrors: (id: string, errors: string[]) => void;
}

export const TabNavigationContext: Context<
  TabNavigationContextType | undefined
> = createContext<TabNavigationContextType | undefined>(undefined);
TabNavigationContext.displayName = "TabNavigationContext";

export function TabNavigationProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [sectionRefs, setSectionRefs] = useState<
    Record<string, { title: string; ref: HTMLElement | null }>
  >({});
  const [sectionErrors, setSectionErrorsState] = useState<
    Record<string, string[]>
  >({});

  const addSectionRef = (
    id: string,
    title: string,
    ref: HTMLElement | null,
  ) => {
    setSectionRefs((prev) => ({ ...prev, [id]: { title, ref } }));
  };

  const removeSectionRef = (id: string) => {
    setSectionRefs((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const setSectionErrors = (id: string, errors: string[]) => {
    setSectionErrorsState((prev) => {
      if (errors.length === 0 && !prev[id]) {
        return prev;
      }
      if (
        errors.length === prev[id]?.length &&
        errors.every((e, i) => e === prev[id]?.[i])
      ) {
        return prev;
      }
      return { ...prev, [id]: errors };
    });
  };

  return (
    <TabNavigationContext.Provider
      value={{
        sectionRefs,
        addSectionRef,
        removeSectionRef,
        sectionErrors,
        setSectionErrors,
      }}
    >
      {children}
    </TabNavigationContext.Provider>
  );
}
